import React, { useSyncExternalStore, cloneElement, ReactElement, memo } from 'react'
import { createRoot, Root } from 'react-dom/client'
import { createPortal } from 'react-dom'
import { omit } from 'lodash'
import { ChakraWrapper, ChakraWrapperProps } from '~/components/ChakraWrapper'
import createDebugLogger from '~/common/logger'
import { useIntlContext } from '~/components/IntlProviders.context'

const debug = createDebugLogger('SharedReactRootManager')

// Type for registered components
interface RegisteredComponent {
  container: Element
  reactNode: ReactElement
  wrapperNode: ReactElement
  props?: Record<string, any>
}

// Store implementation for managing registered components
class ComponentRegistry {
  private registry: Record<string, RegisteredComponent> = {}

  private registryListeners = new Set<() => void>()

  private componentListeners: Record<string, Set<() => void>> = {}

  /// //////////
  // REGISTRY //
  /// //////////
  subscribeToRegistry = (listener: () => void) => {
    this.registryListeners.add(listener)
    return () => this.registryListeners.delete(listener)
  }

  getSnapshotOfRegistry = () => this.registry

  unregisterComponent = (key: string) => {
    if (this.registry[key]) {
      debug('unregisterComponent', key)
      this.registry = omit(this.registry, key)
      this.notifyRegistryListeners()
    }
  }

  updateRegistry = (key: string, registeredComponent: Omit<RegisteredComponent, 'wrapperNode'>) => {
    // If the component is already registered with the same container, just update it
    if (this.registry[key] && this.registry[key].container === registeredComponent.container) {
      this.updateComponent(key, registeredComponent)
      return
    }

    debug('registerComponent', key, registeredComponent.props)
    const Component = makeRegisteredComponent(key)
    this.registry = { ...this.registry, [key]: { ...registeredComponent, wrapperNode: <Component /> } }
    this.notifyRegistryListeners()
  }

  private notifyRegistryListeners = () => {
    this.registryListeners.forEach(listener => listener())
  }

  /// ////////////
  // COMPONENTS //
  /// ////////////
  subscribeToComponent = (key: string) => (listener: () => void) => {
    if (!this.componentListeners[key]) {
      this.componentListeners[key] = new Set()
    }
    this.componentListeners[key].add(listener)
    return () => this.componentListeners[key].delete(listener)
  }

  getSnapshotOfComponent = (key: string) => this.registry[key]

  updateComponent = (key: string, registeredComponent: Partial<RegisteredComponent>) => {
    if (this.registry[key]) {
      this.registry[key] = {
        ...this.registry[key],
        ...registeredComponent,
        props: { ...this.registry[key].props, ...registeredComponent.props }
      }
      debug('updateComponent', key, registeredComponent.props)
      this.notifyComponentListeners(key)
    }
  }

  private notifyComponentListeners = (key: string) => {
    this.componentListeners[key]?.forEach(listener => listener())
  }
}

// Singleton instance of the registry
let registry = new ComponentRegistry()

function makeRegisteredComponent(key: string) {
  function RegisteredComponent() {
    const subscribedComponent = useSyncExternalStore(registry.subscribeToComponent(key), () =>
      registry.getSnapshotOfComponent(key)
    )
    debug('RegisteredComponent render', key, subscribedComponent.reactNode, subscribedComponent.props)
    return cloneElement(subscribedComponent.reactNode, subscribedComponent.props)
  }

  return memo(RegisteredComponent)
}

function ComponentManager() {
  // Subscribe to registry updates via useSyncExternalStore
  // Whenever register.registerComponent is called, this component will re-render
  const cmpRegistry = useSyncExternalStore(registry.subscribeToRegistry, registry.getSnapshotOfRegistry)

  debug(() => [
    'ComponentManager',
    ...Object.entries(cmpRegistry).map(([key, cmp]) => ({
      key,
      ...cmp,
      container: (cmp.container.cloneNode(false) as Element).outerHTML
    }))
  ])

  // Some components have aggressive on-load translations with useMsg(), don't render until we have full intl
  const { allLayersLoaded } = useIntlContext()
  if (!allLayersLoaded) {
    return null
  }

  return (
    <>
      {Object.entries(cmpRegistry)
        .filter(([, { container }]) => container)
        .map(([key, { container, wrapperNode }]) => createPortal(wrapperNode, container, key))}
    </>
  )
}

// Single React root instance
let root: Root | null = null
let hmrEnabled = false

/**
 * Render a React component into a shared React root. Meant only for use on legacy Django pages.
 *
 * You can render multiple components with this util in various places in a vanilla js file and
 * they will all render into the same root, and they will be managed by the SharedReactRootManager.
 *
 * @example
 * ```js
 * // In a Webpack Entry file, e.g. assets/js/my_component.js
 * import { renderIntoSharedReactRoot } from '~/common/SharedReactRootManager'
 *
 * window.renderMyComponent = () => {
 *   renderIntoSharedReactRoot(<MyComponent />, document.querySelector('[data-react="MyComponent"]'))
 * }
 * ```
 * ```js
 * // In webpack.config.js in the entry object
 * entry: filterEntries({
 *   ...
 *   my_webpack_entry_name: './assets/js/my_component',
 *   ...
 * })
 * ```
 * ```html
 * <!-- In a Django template -->
 * {% load render_bundle from webpack_loader %}
 * {% render_bundle 'my_webpack_entry_name' %}
 *
 * <div data-react="MyComponent"></div>
 * <script>
 *   window.renderMyComponent()
 *
 *   // You can also use the returned object to update or unmount the component
 *   const cmp = window.renderMyComponent()
 *   cmp.update({ newProp: 'newValue' })
 *   cmp.unmount()
 * </script>
 * ```
 *
 * @param reactNode The React component to render
 * @param container The DOM element to render the component into
 * @param options
 * @param options.remount If true, the component will be fully remounted. Good for cases where the component needs to be re-initialized.
 * @param options.chakraWrapperProps Props to pass to the ChakraWrapper component
 * @param options.nodeName The name of the component to use as a key
 *
 * @returns A function to update the component with new props.
 */
export function renderIntoSharedReactRoot(
  reactNode: ReactElement,
  container: Element | null,
  {
    remount = false,
    chakraWrapperProps,
    nodeName: _nodeName
  }: {
    remount?: boolean
    chakraWrapperProps?: Omit<ChakraWrapperProps, 'children'>
    nodeName?: string
  } = {}
) {
  if (!container) {
    throw Error('No container provided. Please provide a container element to render into.')
  }

  // Initialize React root if not already created
  if (!root) {
    debug('createRoot for React')
    root = createRoot(ensureReactRoot())
    root.render(
      <ChakraWrapper {...chakraWrapperProps}>
        <ComponentManager />
      </ChakraWrapper>
    )
  }

  // Extract an id for the container
  const containerId: string = container.getAttribute('data-react') || container.id

  // Extract a name from the node
  const nodeName: string = _nodeName || (reactNode as any)?.type?.displayName || (reactNode as any)?.type?.name

  const key = [nodeName, containerId].filter(Boolean).join('-')

  if (!key) {
    throw new Error(
      'Could not extract a usable key. A key is required for renderIntoSharedReactRoot. Please provider a `key` option.'
    )
  }

  if (remount) {
    registry.unregisterComponent(key)
  }

  registry.updateRegistry(key, { reactNode, container, props: reactNode.props })

  // Enable HMR (Hot Module Replacement) if using Webpack
  if (!hmrEnabled && typeof module !== 'undefined' && module.hot) {
    hmrEnabled = true
    module.hot.accept(() => {
      debug('SharedReactRootManager HMR')
      root?.render(
        <ChakraWrapper {...chakraWrapperProps}>
          <ComponentManager />
        </ChakraWrapper>
      )
    })
  }

  return {
    update: (props: any) =>
      registry.updateComponent(key, {
        props
      }),
    unmount: () => {
      registry.unregisterComponent(key)
    }
  }
}

// Ensure the root div exists in the DOM
function ensureReactRoot(): HTMLElement {
  let rootContainer = document.getElementById('react-root')

  if (!rootContainer) {
    rootContainer = document.createElement('div')
    rootContainer.id = 'react-root'
    document.body.appendChild(rootContainer)
  }

  return rootContainer
}

export function _resetReactRoot() {
  if (root) {
    root.unmount()
    root = null
    registry = new ComponentRegistry()
    hmrEnabled = false
  }
}
